// ── COME BACK HERE AFTER SIGNING UP ──────────────────────────────────────────
// A visitor browsing a profile signed out gets stopped at Like or Message. The
// account they then create is a means to an end — the end being the page they
// were already on — so where they were is written down before the sign-up
// starts and read back once it finishes.
//
// LOCALSTORAGE RATHER THAN ROUTER STATE. `state.from` is what the in-app
// hand-offs already pass around (AccountType → Settings → back), and it is used
// here too; but it lives in the history entry, so a reload, a tab restore, or
// an OAuth round trip that replaces the page loses it. Sign-up is exactly the
// journey where those happen. This survives them.
//
// WHO CLEARS IT. Whoever acts on it — the gate modal when the account is
// finished on the spot, AccountType when it hands the destination forward, the
// modal itself when it is dismissed without registering. A path left lying
// around here would eventually redirect somebody who never asked to go there,
// which is why nothing merely reads it and moves on.

const KEY = 'tunify:returnTo';

// Stale by any reasonable measure. Somebody who abandoned a sign-up yesterday
// and signs up today is starting a new journey, not resuming that one.
const MAX_AGE_MS = 30 * 60 * 1000;

// The auth screens themselves, which are never a destination: returning a
// freshly registered user to /register is a loop.
const BLOCKED = /^\/(login|register|account-type|forgot-password)(?:[/?#]|$)/;

export function rememberReturnTo(path) {
  const p = String(path || '');
  // In-app absolute paths only. `//host` is a protocol-relative URL and would
  // send someone off-site; an absolute URL likewise.
  if (!p.startsWith('/') || p.startsWith('//') || BLOCKED.test(p)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path: p, at: Date.now() }));
  } catch { /* private mode, quota — not worth failing a click over */ }
}

export function peekReturnTo() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return '';
    const { path, at } = JSON.parse(raw);
    if (!path || !at || Date.now() - at > MAX_AGE_MS) { clearReturnTo(); return ''; }
    // Re-checked on the way out, not only on the way in: the value could have
    // been written by an older build, or edited by hand.
    if (!String(path).startsWith('/') || String(path).startsWith('//')) { clearReturnTo(); return ''; }
    return path;
  } catch {
    return '';
  }
}

export function clearReturnTo() {
  try { localStorage.removeItem(KEY); } catch { /* see above */ }
}

// Read and consume in one step, for the caller that is about to act on it.
export function takeReturnTo() {
  const v = peekReturnTo();
  clearReturnTo();
  return v;
}
